/**
 * Classification domain types matching backend schemas.
 * Source: services/api/app/domain/classifications/enums.py
 *         services/api/app/schemas/classifications.py
 *         services/api/app/domain/classifications/extracted_fields.py
 *         services/api/app/domain/classifications/classification_result.py
 */

// ── Shared types ────────────────────────────────────────────────────

export interface UserSummary {
  uuid: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name: string;
  profile_picture_signed_url?: string;
}

// ── Const enums (matching backend StrEnums) ─────────────────────────

export const ClassificationDocumentType = {
  FACTURA: "factura",
  NOMINA: "nomina",
  PRESUPUESTO: "presupuesto",
  PROFORMA: "proforma",
  ALBARAN: "albaran",
  CONTRATO: "contrato",
  COMPROBANTE_PAGO: "comprobante_pago",
  CERTIFICADO: "certificado",
  RIDER: "rider",
  DESCONOCIDO: "desconocido",
} as const;

export type ClassificationDocumentTypeValue =
  (typeof ClassificationDocumentType)[keyof typeof ClassificationDocumentType];

export const EligibilityType = {
  AUTOMATIC: "automatic",
  CONDITIONAL: "conditional",
  NEVER: "never",
} as const;

export type EligibilityTypeValue = (typeof EligibilityType)[keyof typeof EligibilityType];

// ── F4 — F3 3-state eligibility (UPPERCASE — matches PG enum) ──────

export const Eligibility = {
  DEDUCIBLE: "DEDUCIBLE",
  CONDICIONAL: "CONDICIONAL",
  NO_DEDUCIBLE: "NO_DEDUCIBLE",
} as const;

export type EligibilityValue = (typeof Eligibility)[keyof typeof Eligibility];

export const Destinatario = {
  ARTISTA_NOMBRADO: "artista_nombrado",
  ARTISTAS_GENERICOS: "artistas_genericos",
  CREW: "crew",
  AGENCIA_MANAGEMENT: "agencia_management",
  PROVEEDOR_EXTERNO: "proveedor_externo",
  ESTRUCTURA_ADMIN: "estructura_admin",
  LOCAL_EXPLOTACION: "local_explotacion",
  INVITADO_PROMOCIONAL: "invitado_promocional",
  PUBLICO: "publico",
} as const;

export type DestinatarioValue = (typeof Destinatario)[keyof typeof Destinatario];

export type DestinatarioState = "confident" | "uncertain" | "unknown";

export const ClassificationStatus = {
  QUEUED: "queued",
  PROCESSING: "processing",
  CLASSIFIED: "classified",
  NEEDS_REVIEW: "needs_review",
  DUPLICATE_DETECTED: "duplicate_detected",
  SKIPPED: "skipped",
  FAILED: "failed",
  REJECTED: "rejected",
  DISCARDED: "discarded",
  SPLIT: "split",
} as const;

export type ClassificationStatusValue =
  (typeof ClassificationStatus)[keyof typeof ClassificationStatus];

export const UploadSessionStatus = {
  OPEN: "open",
  PROCESSING: "processing",
  COMPLETED: "completed",
  PARTIAL: "partial",
  EXPIRED: "expired",
} as const;

export type UploadSessionStatusValue =
  (typeof UploadSessionStatus)[keyof typeof UploadSessionStatus];

export const ReviewAction = {
  VALIDATED: "validated",
  CORRECTED: "corrected",
  REJECTED: "rejected",
} as const;

export type ReviewActionValue = (typeof ReviewAction)[keyof typeof ReviewAction];

// ── Extracted fields (per document type) ────────────────────────────

interface InvoiceLineItem {
  description: string | null;
  quantity: number | null;
  unit_price: string | null;
  total: string | null;
}

/**
 * Un bloque Base/Tipo/Cuota del resumen de IVA de una factura multi-tipo
 * (hoteles, suministros, catering). Cuando hay más de un tipo, iva_rate
 * lleva el dominante (mayor base) e iva_amount la cuota total.
 */
export interface IvaBreakdownEntry {
  rate: string | null;
  base: string | null;
  amount: string | null;
}

export interface InvoiceFields {
  invoice_number: string | null;
  invoice_date: string | null;
  due_date: string | null;
  supplier_name: string | null;
  supplier_tax_id: string | null;
  supplier_address: string | null;
  receiver_name: string | null;
  receiver_tax_id: string | null;
  receiver_address: string | null;
  subtotal: string | null;
  currency: string | null;
  iva_rate: string | null;
  iva_amount: string | null;
  iva_breakdown: IvaBreakdownEntry[];
  irpf_rate: string | null;
  irpf_amount: string | null;
  total_amount: string | null;
  concept_summary: string | null;
  line_items: InvoiceLineItem[];
  beneficiary_name: string | null;
}

export interface PayrollFields {
  company_name: string | null;
  company_tax_id: string | null;
  company_ccc: string | null;
  worker_name: string | null;
  worker_tax_id: string | null;
  worker_nss: string | null;
  worker_position: string | null;
  worker_category: string | null;
  worker_start_date: string | null;
  contract_type: string | null;
  period_month: number | null;
  period_year: number | null;
  period_start: string | null;
  period_end: string | null;
  /**
   * Tipo de liquidación: "ordinaria" o "extraordinaria" (paga extra).
   * Discrimina la paga extra de la nómina ordinaria del mismo mes en la
   * detección de duplicados.
   */
  payroll_type: string | null;
  base_salary: string | null;
  seniority_bonus: string | null;
  extra_payments: string | null;
  overtime_amount: string | null;
  other_earnings: string | null;
  total_earnings: string | null;
  ss_worker_contribution: string | null;
  irpf_rate: string | null;
  irpf_amount: string | null;
  other_deductions: string | null;
  total_deductions: string | null;
  net_salary: string | null;
  currency: string | null;
  ss_employer_contribution: string | null;
  /**
   * Coste laboral total para la empresa. Preferido como base de Art. 36 LIS
   * cuando el PDF lo trae literal. Si no, el resolver lo recompone como
   * total_earnings + ss_employer_contribution.
   */
  coste_empresa: string | null;
}

export interface ContractFields {
  parties: string[];
  service_description: string | null;
  total_fee: string | null;
  currency: string | null;
  start_date: string | null;
  end_date: string | null;
  payment_terms: string | null;
}

export interface PaymentFields {
  amount: string | null;
  currency: string | null;
  transfer_date: string | null;
  beneficiary_name: string | null;
  beneficiary_tax_id: string | null;
  ordering_party: string | null;
  bank_name: string | null;
  invoice_reference: string | null;
  transfer_reference: string | null;
  concept: string | null;
}

export interface CertificateFields {
  issuing_authority: string | null;
  certificate_type: string | null;
  modelo_number: string | null;
  tax_period: string | null;
  amount: string | null;
  currency: string | null;
  subject_tax_id: string | null;
  subject_name: string | null;
}

export type ExtractedFields =
  | InvoiceFields
  | PayrollFields
  | ContractFields
  | PaymentFields
  | CertificateFields;

// ── Classification result (categorization + qualification) ──────────

export interface CategorizationData {
  category_id: string | null;
  category_name: string | null;
  subcategory_id: string | null;
  subcategory_name: string | null;
  confidence: number | null;
  method: string | null;
  flags: string[];
}

export interface QualificationData {
  eligible: boolean | null;
  eligibility_type: EligibilityTypeValue | null;
  fiscal_category: string | null;
  fiscal_subcategory: string | null;
  article: string | null;
  reasoning: string | null;
}

export interface PipelineErrorData {
  failed_stage: string | null;
  is_retriable: boolean;
  error: string | null;
  user_message: string | null;
}

export interface DuplicateMatchData {
  classification_uuid: string;
  match_type: string;
  matched_fields: string[];
}

export interface DuplicateDetectionData {
  has_duplicates: boolean;
  matches: DuplicateMatchData[];
}

export interface ClassificationResultData {
  categorization: CategorizationData | null;
  qualification: QualificationData | null;
  pipeline_error?: PipelineErrorData;
  duplicate_detection?: DuplicateDetectionData;
}

// ── F4 — F3 eligibility decision (mirror of services/api EligibilityDecisionResponse)

export interface EligibilityDecision {
  eligibility: EligibilityValue;
  operational_category: string | null;
  destinatario: DestinatarioValue | null;
  destinatario_state: DestinatarioState | null;
  destinatario_confidence: string | null;
  fiscal_category: string | null;
  fiscal_subcategory: string | null;
  rule_id: string | null;
  rationale: string | null;
  review_question: string | null;
  missing_evidences: string[];
  // evidence_context is intentionally NOT exposed in the UI per fase4-plan
  // OQ-3 decision — it is a debugging payload for internal tooling.
  evidence_context: Record<string, unknown> | null;
  /** Base imponible Art. 36 LIS — single source of truth used by all
   * aggregators (overview, fiscal-quota, suppliers). Read-only; the API
   * recomputes it from extraction. The deduction percentage lives on the
   * fiscal_quota and is applied at quota level. */
  deductible_base_amount: string | null;
  ruleset_version: string | null;
  llm_explanation: string | null;
}

/**
 * ── F4 (#156) — review_reasons tagged-union shape
 *
 * The API normalises legacy raw strings and unknown codes into the
 * PIPELINE_PARTIAL form on serialisation, so the frontend always sees
 * `{code, params}` items. `ReviewReasonItem` is kept as an alias so older
 * imports keep working — it is no longer a union.
 */

export type ReviewReasonCode =
  | "categorize_out_of_vocab"
  | "categorize_low_confidence"
  | "categorize_concept_extraction_failed"
  | "categorize_no_ruleset"
  | "categorize_llm_failed"
  | "resolve_known_category_no_destinatario"
  | "resolve_destinatario_uncertain"
  | "resolve_no_rule_for_combination"
  | "resolve_missing_evidences"
  | "resolve_rule_question"
  | "normalization_partial"
  | "pipeline_partial"
  | "duplicate_detected";

export interface ReviewReason {
  code: ReviewReasonCode | string;
  params?: Record<string, unknown>;
}

export type ReviewReasonItem = ReviewReason;

// ── #313 — review flags (per-finding lifecycle) ─────────────────────

export const ReviewFlagSeverity = {
  BLOCKING: "BLOCKING",
  ADVISORY: "ADVISORY",
} as const;

export type ReviewFlagSeverityValue = (typeof ReviewFlagSeverity)[keyof typeof ReviewFlagSeverity];

export const ReviewFlagResolution = {
  ACCEPTED: "ACCEPTED",
  RESOLVED: "RESOLVED",
  SUPERSEDED: "SUPERSEDED",
} as const;

export type ReviewFlagResolutionValue =
  (typeof ReviewFlagResolution)[keyof typeof ReviewFlagResolution];

/**
 * Un hallazgo de revisión con su ciclo de vida (#313). `is_open` y
 * `severity` los deriva el backend; un flag BLOCKING abierto excluye la fila
 * del cálculo fiscal y bloquea la aprobación hasta resolverlo por su flujo.
 */
export interface ReviewFlag {
  code: ReviewReasonCode | string;
  params: Record<string, unknown>;
  severity: ReviewFlagSeverityValue;
  is_open: boolean;
  raised_at: string;
  resolved_at: string | null;
  resolution: ReviewFlagResolutionValue | null;
  resolved_by_id: number | null;
}

// ── #313 — human disposition over the row's content ─────────────────

export const ClassificationDisposition = {
  NONE: "NONE",
  VALIDATED: "VALIDATED",
  REJECTED: "REJECTED",
  DISCARDED: "DISCARDED",
} as const;

export type ClassificationDispositionValue =
  (typeof ClassificationDisposition)[keyof typeof ClassificationDisposition];

// ── API response types ──────────────────────────────────────────────

export interface ClassificationResponse {
  uuid: string;
  status: ClassificationStatusValue;
  document_type: ClassificationDocumentTypeValue | null;
  source_filename: string;
  // Linaje — presentes cuando el documento es hijo de un split multipágina
  parent_document_uuid: string | null;
  parent_document_name: string | null;
  extracted_fields: ExtractedFields | null;
  classification_result: ClassificationResultData | null;
  expense_type: string | null;
  expense_subtype: string | null;
  fiscal_category: string | null;
  eligible: boolean | null;
  confidence: string | null;
  total_amount: string | null;
  deductible_base_amount: string | null;
  currency: string;
  document_date: string | null;
  requires_review: boolean;
  review_reasons: ReviewReason[];
  // #313 — live per-finding lifecycle; the UI drives off these.
  review_flags: ReviewFlag[];
  // #313 — human decision over the row's content, orthogonal to `status`.
  disposition: ClassificationDispositionValue;
  review_action: ReviewActionValue | null;
  reviewed_by_id: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  corrected_fields: Record<string, unknown> | null;
  processing_time_ms: number | null;
  tokens_used: number;
  created_at: string;
  processed_at: string | null;
  uploaded_by_user: UserSummary | null;
  reviewed_by_user: UserSummary | null;
  // F4 — null on pre-F3 rows (legacy `eligible: bool` is the fallback).
  eligibility_decision: EligibilityDecision | null;
  // Sticky ground-truth locks the user set for smart reprocessing.
  field_locks: FieldLock[];
}

// ── Field locks (smart reprocessing) ──────────────────────────────────

export interface FieldLock {
  field_key: string;
  value: string;
  created_by_id: number | null;
  created_at: string | null;
}

export interface LockableField {
  field_key: string;
  label: string;
  value_type: string; // "document_type" | "amount" | "date" | "text"
  current_value: string | null;
}

export interface ClassificationLocksResponse {
  locks: FieldLock[];
  lockable_fields: LockableField[];
}

export interface FieldLockInput {
  field_key: string;
  value: string;
}

export interface ReclassifyClassificationRequest {
  locks: FieldLockInput[];
}

export interface ClassificationListResponse {
  classifications: ClassificationResponse[];
  total_count: number;
  total_unfiltered_count: number;
  limit: number;
  offset: number;
}

export interface ClassificationListParams {
  status?: ClassificationStatusValue;
  document_type?: ClassificationDocumentTypeValue | ClassificationDocumentTypeValue[];
  search?: string;
  eligible?: boolean;
  expense_type?: string;
  // F4 filters
  eligibility?: EligibilityValue;
  requires_review?: boolean;
  rule_id?: string;
  fiscal_quota_uuid?: string;
  /** C3 — multi-select; sent as repeated `fiscal_category=` query params. */
  fiscal_category?: string[];
  /** Split multipágina — solo las hijas de este documento padre. */
  parent_document_uuid?: string;
  sort_by?:
    | "created_at"
    | "document_type"
    | "total_amount"
    | "status"
    | "document_date"
    | "filename";
  sort_direction?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

/**
 * Filters consumed by the classifier export modal. The shape mirrors the
 * classifier list filters plus `beneficiary_uuid`, which travels in the
 * filters object so the generic `IExportRepository<TFilters>` contract stays
 * unchanged across entities.
 */
export interface ClassificationExportParams {
  beneficiary_uuid: string;
  status?: ClassificationStatusValue;
  document_type?: ClassificationDocumentTypeValue[];
  search?: string;
  eligibility?: EligibilityValue;
  requires_review?: boolean;
  rule_id?: string;
  fiscal_quota_uuid?: string;
  fiscal_category?: string[];
  expense_type?: string;
  sort_by?: string;
  sort_direction?: "asc" | "desc" | null;
}

// ── F4 — rule-ids list shape (async dropdown source) ─────────

export interface RuleIdCount {
  rule_id: string;
  count: number;
}

export interface RuleIdsResponse {
  rule_ids: RuleIdCount[];
}

// ── Upload session types ────────────────────────────────────────────

export interface UploadItem {
  classification_uuid: string;
  filename: string;
  /** Signed PUT URL. Null for deferred sessions — requested later in
   *  batches via batch signing. */
  upload_url: string | null;
  gcs_path: string;
  content_type: string;
}

export interface CreateUploadSessionRequest {
  filenames: string[];
  /** UUID of the fiscal quota the uploaded documents belong to. Required by the API
   *  so the classifier can resolve project + fiscal-year context from it. */
  fiscal_quota_uuid: string;
  /** When true the session is created without signing URLs; the client signs
   *  them in batches of MAX_SIGN_BATCH_SIZE. Lifts the 500-file inline cap. */
  deferred?: boolean;
}

export interface CreateUploadSessionResponse {
  session_id: string;
  expires_at: string;
  uploads: UploadItem[];
}

export interface SignUploadBatchRequest {
  classification_uuids: string[];
}

export interface SignedUpload {
  classification_uuid: string;
  upload_url: string;
  content_type: string;
}

export interface SignUploadBatchResponse {
  session_id: string;
  expires_at: string;
  uploads: SignedUpload[];
}

export interface UploadSessionStatusResponse {
  session_id: string;
  status: UploadSessionStatusValue;
  expected_count: number;
  received_count: number;
  completed_count: number;
  failed_count: number;
  needs_review_count: number;
  created_at: string;
  expires_at: string;
  completed_at: string | null;
}

// ── Review types ────────────────────────────────────────────────────

export interface ReviewClassificationRequest {
  action: ReviewActionValue;
}

// ── File response ───────────────────────────────────────────────────

export interface ClassificationFileResponse {
  signed_url: string;
  content_type: string | null;
  size_bytes: number | null;
  uploaded_at: string | null;
  filename: string | null;
}

// ── Suppliers analytics ─────────────────────────────────────────────

export interface SupplierBreakdown {
  name: string;
  tax_id: string | null;
  invoice_count: number;
  total: string;
}

export interface SuppliersAnalyticsResponse {
  suppliers: SupplierBreakdown[];
  total_suppliers: number;
  top3_concentration: string;
}

// ── Summary response (restructured) ────────────────────────────────

export interface FinancialMetrics {
  total_spend: string;
  deductible_spend: string;
  non_deductible_spend: string;
  conditional_spend: string;
  conditional_count: number;
  estimated_savings: string;
}

export interface DocumentTypeCount {
  document_type: string;
  count: number;
  total: string;
}

export interface DocumentInventory {
  total_count: number;
  by_type: DocumentTypeCount[];
}

export interface ConciliationMetrics {
  invoices_count: number;
  invoices_total: string;
  payment_proofs_count: number;
  payment_proofs_total: string;
}

export interface MonthlyBreakdown {
  month: number;
  deductible: string;
  non_deductible: string;
}

export interface CategoryBreakdown {
  fiscal_category: string;
  fiscal_category_name: string;
  total: string; // net of VAT, eligible + still-pending
  percentage: string;
  eligible_count: number;
  eligible_amount: string; // net of VAT, ART. 36 LIS-counting expense
  total_count: number;
}

/** Pipeline-status counts powering the operational "Estado de
 * clasificación" panel in the beneficiary overview. Returned by
 * /summary and intentionally NOT filtered by the analytics pills so
 * failed / duplicate rows can never be hidden by the active view. */
export interface PipelineStatusBreakdown {
  queued: number; // QUEUED
  processing: number; // PROCESSING
  failed: number;
  classified: number;
  needs_review: number;
  duplicate_detected: number;
}

export interface ClassificationSummaryResponse {
  financials: FinancialMetrics;
  document_inventory: DocumentInventory;
  conciliation: ConciliationMetrics;
  monthly: MonthlyBreakdown[];
  categories: CategoryBreakdown[];
  pipeline_status: PipelineStatusBreakdown;
}

// ── Tax breakdown analytics ─────────────────────────────────────────

export interface IvaRateBreakdown {
  rate: string;
  taxable_base: string;
  amount: string;
  invoice_count: number;
}

export interface TaxBreakdownAnalyticsResponse {
  total_iva: string;
  total_irpf: string;
  iva_by_rate: IvaRateBreakdown[];
}

// ── Workforce analytics ─────────────────────────────────────────────

export interface WorkerBreakdown {
  tax_id: string;
  name: string;
  position: string | null;
  total_earnings: string;
  months: number;
}

export interface WorkforceAnalyticsResponse {
  total_payroll: string;
  worker_count: number;
  workers: WorkerBreakdown[];
}
